import { Box, Button, Grid2, Typography } from "@mui/material";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import RoundedTextField from "../../components/RoundedPaymentTextField";
import { useState } from "react";
import WhiteStar from "../../assets/images/Star 2.svg";
import FlightIcon from "../../assets/images/flight-icon-with-bg.png";
import BackButton from "../../components/BackButton";
import SuccessModal from "../../components/SuccessModal";
import { baseFetch } from "../../helpers/apiClient";
import { useDispatch } from "react-redux";
import { useSnackbar } from "../../context/SnackbarContext";
import Loader from "../../components/Loader";
import { toggleSessionModal } from "../../redux/sessionSlice";

const PurchaseContentRetention = ({
  suggestedRetentionPlan,
  setPaymentMode,
  selectedPlan,
}) => {
  const [subsError, setSubsError] = useState("");
  const [values, setValues] = useState({
    cardholderName: "",
    cardNumber: false,
    cvv: false,
    expiry: false,
  });
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const handleChange = (name, value) => {
    subsError && setSubsError("");
    if (name === "cardholderName") {
      if (/^[A-Za-z\s]+$/.test(value) || value === "") {
        setValues((prevFields) => ({
          ...prevFields,
          [name]: value,
        }));
      } else {
        setValues((prevFields) => ({
          ...prevFields,
          [name]: prevFields.cardholderName,
        }));
      }
    } else if (value.length > 20) {
      return;
    } else {
      setValues((prevFields) => ({
        ...prevFields,
        [name]: value,
      }));
    }
  };

  const stripeInputStyle = {
    base: {
      fontSize: "16px",
      fontWeight: 400,
      color: "#2A4876",
      "::placeholder": {
        color: "#989899",
        fontSize: "14px",
      },
    },
    invalid: {
      color: "#fa755a",
    },
  };

  const handlePurchase = async () => {
    setLoading(true);
    if (!stripe || !elements) return;

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardNumberElement),
    });

    if (error) {
      console.log(error.message);
      showSnackbar({ message: error.message });
      setLoading(false);
      return;
    }

    const payload = {
      newRetentionPriceId: selectedPlan?.id,
      paymentMethodId: paymentMethod?.id,
    };

    baseFetch({
      url: "/v1/web/retention/purchase",
      method: "POST",
      body: payload,
      dispatch,
    })
      .then(async () => {
        setOpenSuccessModal(true);
      })
      .catch((err) => {
        showSnackbar({ message: err });
        if (err === "Please authenticate") {
          dispatch(toggleSessionModal(false));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box
      sx={{
        overflow: "none",
        padding: "36px",
        borderRadius: "20px",
        backgroundColor: "#fff",
        boxShadow: "0px 20px 20px 0px #2A487610",
        textAlign: "left",
        height: { xs: "auto", md: "76vh" },
        marginBottom: { xs: "30px", md: 0 },
        position: "relative",
      }}
    >
      {loading && <Loader />}
      <Box>
        <BackButton
          leftNormal={true}
          onBackStep={() => setPaymentMode(false)}
        />
      </Box>
      <Typography
        variant="h5"
        sx={{
          color: "#2A4876",
          fontWeight: "bold",
          marginLeft: { xs: 0, sm: "12vw", md: "8vw", lg: "5.5vw" },
        }}
      >
        Content Retention
      </Typography>
      <Grid2 container spacing={2}>
        <Grid2 item size={{ xs: 12, sm: 6 }}>
          <Box sx={{ marginTop: "15px" }}>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                color: "#152134",
                marginBottom: "5px",
              }}
            >
              Enter Card Holder Name
            </Typography>
            <RoundedTextField
              name="cardholderName"
              variant="outlined"
              placeholder="Enter here"
              fullWidth
              type="text"
              value={values?.cardholderName}
              onChange={(e) => handleChange("cardholderName", e.target.value)}
            />
          </Box>
          <Box sx={{ marginTop: "10px" }}>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                color: "#152134",
                marginBottom: "5px",
              }}
            >
              Enter Card Number
            </Typography>
            <CardNumberElement
              className="input-box stripe"
              onChange={(event) =>
                handleChange("cardNumber", event.error ? "" : "filled")
              }
              options={{
                style: stripeInputStyle,
                placeholder: "Enter here",
              }}
            />
          </Box>
          <Box
            sx={{
              flexDirection: {
                xs: "row",
                sm: "column",
                md: "row",
              },
            }}
            className="flex gap-x-4 mt-4"
          >
            <Box className="w-full">
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "#152134",
                  marginBottom: "5px",
                }}
              >
                Enter Expiry Date
              </Typography>
              <div className="input-box stripe">
                <CardExpiryElement
                  onChange={(event) =>
                    handleChange("expiry", event.error ? "" : "filled")
                  }
                  options={{
                    style: stripeInputStyle,
                    placeholder: "Enter here",
                  }}
                />
              </div>
            </Box>
            <Box className="w-full">
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "#152134",
                  marginBottom: "5px",
                }}
              >
                Enter CVV
              </Typography>
              <CardCvcElement
                className="input-box stripe"
                onChange={(event) =>
                  handleChange("cvv", event.error ? "" : "filled")
                }
                options={{
                  style: stripeInputStyle,
                  placeholder: "Enter here",
                }}
              />
            </Box>
          </Box>
          <Button
            onClick={handlePurchase}
            disabled={
              !values.cardholderName ||
              !values.cardNumber ||
              !values.cvv ||
              !values.expiry ||
              subsError
            }
            variant="contained"
            className="Send-button"
            sx={{
              textTransform: "none",
              backgroundColor: "#2A4876",
              color: "#fff",
              marginTop: {
                xs: "25px !important",
                sm: "25px !important",
                md: "5vh !important",
                lg: "10vh !important",
              },
              borderRadius: "50px",
              width: "20vw !important",
            }}
          >
            Buy!
          </Button>
        </Grid2>
        <Grid2 item size={{ xs: 12, sm: 6 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: {
                xs: "row",
                sm: "column",
                md: "row",
              },
              justifyContent:
                selectedPlan?.id !== suggestedRetentionPlan?.id
                  ? "flex-start"
                  : "space-between",
              marginTop: "15px",
              marginBottom: "10px",
              backgroundColor: "#F1F7FA",
              padding: "10px",
              borderRadius: "15px",
              gap: "5px",
            }}
          >
            <Box
              sx={{
                width: "21px",
                height: "21px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
              }}
            >
              <img
                src={FlightIcon}
                style={{ width: "21px", height: "21px", marginRight: "8px" }}
              />
            </Box>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "14px",
                color: "#2A4876",
                ...(selectedPlan?.id !== suggestedRetentionPlan?.id && {
                  marginLeft: "10px",
                }),
              }}
            >
              {selectedPlan.nickname} (
              {selectedPlan.contentSize.min === 0
                ? 1
                : selectedPlan.contentSize.min === 16
                  ? "16+"
                  : selectedPlan.contentSize.min}{" "}
              {selectedPlan.contentSize.max && "-"}{" "}
              {selectedPlan.contentSize.max} pieces) - $
              {selectedPlan.unit_amount / 100} / month
            </Typography>
            {selectedPlan?.id === suggestedRetentionPlan?.id && (
              <Box
                sx={{
                  display: "flex",
                  gap: "2px",
                  backgroundColor: "#42C66C",
                  padding: "5px",
                  borderRadius: "8px",
                  alignItems: "center",
                  marginTop: {
                    xs: 0,
                    sm: "10px",
                    md: 0,
                  },
                  width: {
                    xs: "auto",
                    sm: "65%",
                    md: "auto",
                  },
                  height: "max-content",
                  justifyContent: "center",
                }}
              >
                <img src={WhiteStar} />
                <Typography sx={{ color: "white", fontSize: "13px" }}>
                  Recommended
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "10px",
            }}
          >
            <Button
              variant="outlined"
              sx={{
                textTransform: "capitalize",
                borderRadius: "15px",
                fontWeight: "bold",
                color: "#2A4876",
                marginBottom: "20px",
              }}
              onClick={() => setPaymentMode(false)}
            >
              Change
            </Button>
          </Box>
          <hr />
          <Box sx={{ marginTop: "12px" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "10px",
              }}
            >
              <Typography>Reservation Fee</Typography>
              <Typography>${selectedPlan.unit_amount / 100}</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "10px",
                color: "#2A4876",
              }}
            >
              <Typography sx={{ fontWeight: "bold" }}>Total Amount</Typography>
              <Typography sx={{ fontWeight: "bold" }}>
                ${selectedPlan.unit_amount / 100}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: "14px", marginTop: "10px" }}>
              Your payment details are protected with the highest security
              standards.
            </Typography>
          </Box>
        </Grid2>
      </Grid2>
      {openSuccessModal && (
        <SuccessModal
          title={`${
            suggestedRetentionPlan.nickname || selectedPlan.nickname
          } Content Retention Plan purchased successfully`}
          open={openSuccessModal}
          onClose={() => {
            setOpenSuccessModal(false);
            setPaymentMode(false);
          }}
        />
      )}
    </Box>
  );
};

export default PurchaseContentRetention;
